export const RIDE_STATUS_FILTERS = [
  { value: 'open', label: 'Open & new requests' },
  { value: 'assigned', label: 'Driver Assigned' },
  { value: 'live', label: 'In trip' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All' },
];

/** Lower number = higher priority on the board (open work first). */
export const BOARD_STATUS_SORT_ORDER = {
  open: 0,
  assigned: 1,
  live: 2,
  completed: 3,
  cancelled: 4,
};

export function compareRidesByBoardPriority(a, b) {
  const pa = BOARD_STATUS_SORT_ORDER[a.boardStatus] ?? 99;
  const pb = BOARD_STATUS_SORT_ORDER[b.boardStatus] ?? 99;
  if (pa !== pb) return pa - pb;
  const ta = new Date(a.scheduled_at || a.created_at || 0).getTime();
  const tb = new Date(b.scheduled_at || b.created_at || 0).getTime();
  return tb - ta;
}

export const RIDES_PAGE_SIZE = 10;

export const RIDES_PAGE_SIZE_OPTIONS = [10, 20, 50];

export function formatRideDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

export function formatRideDateParts(value) {
  if (!value) return { date: '—', time: '' };
  try {
    const d = new Date(value);
    return {
      date: d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }),
      time: d.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    };
  } catch {
    return { date: String(value), time: '' };
  }
}

export function formatRideAddress(address) {
  const text = String(address || '').replace(/\s+/g, ' ').trim();
  return text || '—';
}

export function formatRidePhone(phone, currency = 'INR') {
  const digits = String(phone || '').replace(/\D/g, '');
  if (currency === 'USD') {
    if (digits.length === 10) {
      return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
  }
  if (digits.length === 10) {
    return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  if (digits.length === 12 && digits.startsWith('91')) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  return phone || '—';
}

export function detectRideCurrency(rideOrRow = {}) {
  const explicit = String(
    rideOrRow.currency ||
      rideOrRow.fare_currency ||
      rideOrRow.raw?.currency ||
      rideOrRow.raw?.fare_currency ||
      ''
  )
    .trim()
    .toUpperCase();

  if (explicit) {
    if (['USD', 'US$', '$', 'DOLLAR', 'DOLLARS'].includes(explicit) || explicit.includes('USD')) {
      return 'USD';
    }
    if (
      ['INR', 'RS', 'RS.', '₹', 'RUPEE', 'RUPEES'].includes(explicit) ||
      explicit.includes('INR') ||
      explicit.includes('RUPEE')
    ) {
      return 'INR';
    }
  }

  const payment = String(rideOrRow.payment || rideOrRow.raw?.payment || '').toLowerCase();
  if (payment.includes('usd') || payment.includes('dollar') || payment.includes('$')) return 'USD';
  if (
    payment.includes('inr') ||
    payment.includes('rupee') ||
    payment.includes('₹') ||
    /\brs\b/.test(payment)
  ) {
    return 'INR';
  }

  const fareRaw = String(rideOrRow.raw?.fare ?? rideOrRow.fare ?? '');
  if (fareRaw.includes('$') || /usd/i.test(fareRaw)) return 'USD';
  if (fareRaw.includes('₹') || /(?:^|\s)(?:rs\.?|inr)/i.test(fareRaw)) return 'INR';

  const address = `${rideOrRow.pickup_address || rideOrRow.pickup || ''} ${
    rideOrRow.dropoff_address || rideOrRow.dropoff || ''
  }`.toLowerCase();

  const indiaHints =
    /\b(india|delhi|mumbai|bangalore|bengaluru|hyderabad|chennai|kolkata|pune|gurgaon|gurugram|noida|jaipur|ahmedabad|lucknow|chandigarh|uttar pradesh|maharashtra|karnataka|tamil nadu|telangana|gujarat|rajasthan|kerala|bihar|punjab)\b/;
  const usHints =
    /\b(usa|united states|u\.s\.a|california|texas|florida|arizona|nevada|washington|oregon|illinois|georgia|ohio|michigan|new york|new jersey|north carolina|south carolina|massachusetts|pennsylvania|colorado|utah|virginia|maryland|avenue|street|blvd|boulevard|pkwy|parkway|hwy|highway)\b/;

  const hasIndia = indiaHints.test(address);
  const hasUs = usHints.test(address);
  if (hasUs && !hasIndia) return 'USD';
  if (hasIndia && !hasUs) return 'INR';

  const phone = String(
    rideOrRow.customer_phone || rideOrRow.rider_phone || rideOrRow.phone || ''
  );
  const digits = phone.replace(/\D/g, '');
  if (phone.trim().startsWith('+1') || (digits.length === 11 && digits.startsWith('1'))) {
    return 'USD';
  }
  if (phone.trim().startsWith('+91') || (digits.length === 12 && digits.startsWith('91'))) {
    return 'INR';
  }

  return 'INR';
}

/** Rider market (Saarthi app stores fare in INR; display follows phone/location). */
export function detectRiderCountryIso(rideOrRow = {}) {
  const address = `${rideOrRow.pickup_address || rideOrRow.pickup || ''} ${
    rideOrRow.dropoff_address || rideOrRow.dropoff || ''
  }`.toLowerCase();

  const indiaHints =
    /\b(india|delhi|mumbai|bangalore|bengaluru|hyderabad|chennai|kolkata|pune|gurgaon|gurugram|noida|jaipur|ahmedabad|lucknow|chandigarh|uttar pradesh|maharashtra|karnataka|tamil nadu|telangana|gujarat|rajasthan|kerala|bihar|punjab)\b/;
  const usHints =
    /\b(usa|united states|u\.s\.a|california|texas|florida|arizona|nevada|washington|oregon|illinois|georgia|ohio|michigan|new york|new jersey|north carolina|south carolina|massachusetts|pennsylvania|colorado|utah|virginia|maryland|avenue|street|blvd|boulevard|pkwy|parkway|hwy|highway)\b/;

  const hasIndia = indiaHints.test(address);
  const hasUs = usHints.test(address);
  if (hasUs && !hasIndia) return 'US';
  if (hasIndia && !hasUs) return 'IN';

  const phone = String(
    rideOrRow.customer_phone || rideOrRow.rider_phone || rideOrRow.phone || ''
  );
  const digits = phone.replace(/\D/g, '');
  if (phone.trim().startsWith('+1') || (digits.length === 11 && digits.startsWith('1'))) {
    return 'US';
  }
  if (phone.trim().startsWith('+91') || (digits.length === 12 && digits.startsWith('91'))) {
    return 'IN';
  }

  return 'IN';
}

/** Match taxihiring/lib/currency.ts (INR is source of truth for app fares). */
const INR_PER_USD = 83.5;

export function convertAppFareFromInr(amountInr, countryIso) {
  const iso = (countryIso || 'IN').toUpperCase();
  const n = Number(amountInr);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (iso === 'US') return Math.round((n / INR_PER_USD) * 100) / 100;
  return Math.round(n);
}

export function formatMoneyFromInr(amountInr, countryIso) {
  const iso = (countryIso || 'IN').toUpperCase();
  const converted = convertAppFareFromInr(amountInr, iso);
  if (converted == null) return '—';
  return formatRideMoney(converted, iso === 'US' ? 'USD' : 'INR');
}

/** Primary fare label for admin UI (matches rider app for app bookings). */
export function formatRideFare(ride) {
  const stored = Number(ride?.fare);
  if (!Number.isFinite(stored) || stored <= 0) return '—';

  if (ride?.source === 'app') {
    const iso = ride.riderCountryIso || detectRiderCountryIso(ride);
    return formatMoneyFromInr(stored, iso);
  }

  return formatRideMoney(stored, ride.currency || detectRideCurrency(ride));
}

/** Secondary line: INR stored amount when rider sees converted fare. */
export function formatRideFareInrHint(ride) {
  if (ride?.source !== 'app') return '';
  const iso = (ride.riderCountryIso || detectRiderCountryIso(ride)).toUpperCase();
  if (iso === 'IN') return '';
  const stored = Number(ride.fare);
  if (!Number.isFinite(stored) || stored <= 0) return '';
  return formatRideMoney(stored, 'INR');
}

export function getRideDisplayMarket(ride) {
  if (ride?.source === 'app') {
    const iso = ride.riderCountryIso || detectRiderCountryIso(ride);
    return iso === 'US' ? 'USD' : 'INR';
  }
  return ride.currency || detectRideCurrency(ride);
}

export function formatRideMoney(value, currency = 'INR') {
  const n = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(n) || n <= 0) return '—';

  const code = currency === 'USD' ? 'USD' : 'INR';
  return new Intl.NumberFormat(code === 'USD' ? 'en-US' : 'en-IN', {
    style: 'currency',
    currency: code,
    maximumFractionDigits: code === 'USD' ? 2 : 0,
  }).format(n);
}

/** Normalize trip distance to kilometres from common DB fields. */
export function extractDistanceKm(row = {}) {
  const kmFields = [
    row.distance_km,
    row.trip_distance_km,
    row.estimated_distance_km,
    row.distanceKm,
  ];
  for (const value of kmFields) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }

  const miles = Number(row.distance_miles ?? row.trip_distance_miles ?? row.distanceMiles);
  if (Number.isFinite(miles) && miles > 0) return miles / 0.621371;

  const meters = Number(row.distance_m ?? row.distance_meters ?? row.distanceM);
  if (Number.isFinite(meters) && meters > 0) return meters / 1000;

  const raw = Number(row.distance);
  if (Number.isFinite(raw) && raw > 0) {
    // Values over 500 are usually meters.
    return raw > 500 ? raw / 1000 : raw;
  }

  return null;
}

function roundDistance(value) {
  if (!Number.isFinite(value) || value <= 0) return null;
  return value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
}

/**
 * Show both units. Primary unit follows market: USD → miles, INR → km.
 */
export function formatRideDistance(distanceKm, currency = 'INR') {
  const km = Number(distanceKm);
  if (!Number.isFinite(km) || km <= 0) return '—';

  const kmValue = roundDistance(km);
  const milesValue = roundDistance(km * 0.621371);
  if (kmValue == null || milesValue == null) return '—';

  if (currency === 'USD') {
    return `${milesValue} mi (${kmValue} km)`;
  }
  return `${kmValue} km (${milesValue} mi)`;
}

export function boardBucket(status, driverId, driverName) {
  const s = String(status || '').toLowerCase();
  if (s === 'cancelled') return 'cancelled';
  if (s === 'completed') return 'completed';
  const assigned = Boolean(
    String(driverId || '').trim() || String(driverName || '').trim()
  );
  if (['boarded', 'in_trip'].includes(s)) return 'live';
  if (
    assigned ||
    ['accepted', 'en_route', 'nearby', 'arrived', 'assigned'].includes(s)
  ) {
    return 'assigned';
  }
  if (['requested', 'scheduled', 'pending'].includes(s)) return 'open';
  return 'open';
}

export function rideStatusLabel(ride) {
  const s = String(ride.status || '').toLowerCase();
  if (s === 'cancelled') return 'Cancelled';
  if (s === 'completed') return 'Completed';
  if (ride.boardStatus === 'open') return 'Open request';
  if (ride.boardStatus === 'live') return 'In trip';
  if (s === 'arrived') return 'At pickup';
  if (s === 'en_route' || s === 'nearby') return 'Driver on the way';
  return 'Driver Assigned';
}

export function mapAppRide(row) {
  const driverId = row.driver_id || null;
  const mapped = {
    source: 'app',
    id: String(row.id),
    customer_name: row.rider_name || 'Rider',
    customer_phone: row.rider_phone || '',
    pickup_address: row.pickup || '',
    dropoff_address: row.dropoff || '',
    vehicle_type: row.car_name || row.product || 'Taxi',
    scheduled_at: row.pickup_at || null,
    notes: row.admin_note || null,
    status: row.status || 'requested',
    boardStatus: boardBucket(row.status, driverId, row.driver_name),
    assigned_driver_id: driverId,
    assigned_driver_name: row.driver_name || '',
    assigned_vehicle: row.vehicle_number || row.vehicle_name || '',
    fare: row.fare,
    payment: row.payment,
    product: row.product,
    distance_km: extractDistanceKm(row),
    currency: 'INR',
    riderCountryIso: detectRiderCountryIso({
      customer_phone: row.rider_phone,
      pickup_address: row.pickup,
      dropoff_address: row.dropoff,
    }),
    created_at: row.requested_at || row.created_at || null,
    raw: row,
  };
  return mapped;
}

export function mapWebRide(row) {
  const driverId = row.assigned_driver_id || null;
  const mapped = {
    source: 'web',
    id: String(row.id),
    customer_name: row.customer_name || 'Customer',
    customer_phone: row.customer_phone || '',
    pickup_address: row.pickup_address || '',
    dropoff_address: row.dropoff_address || '',
    vehicle_type: row.vehicle_type || 'Any',
    scheduled_at: row.scheduled_at || null,
    notes: row.notes || row.admin_notes || null,
    status: row.status || 'pending',
    boardStatus: boardBucket(row.status === 'pending' ? 'requested' : row.status, driverId),
    assigned_driver_id: driverId,
    assigned_driver_name: '',
    assigned_vehicle: '',
    fare: row.fare ?? row.estimated_fare ?? null,
    payment: row.payment || row.payment_method || null,
    product: 'website',
    distance_km: extractDistanceKm(row),
    currency: null,
    created_at: row.created_at || null,
    raw: row,
  };
  mapped.currency = detectRideCurrency(mapped);
  return mapped;
}

export const APP_RIDE_SELECT =
  'id,rider_name,rider_phone,pickup,dropoff,pickup_at,requested_at,status,driver_id,driver_name,vehicle_number,vehicle_name,car_name,fare,payment,product,admin_note,distance_km';

export const WEB_RIDE_SELECT =
  'id,customer_name,customer_phone,pickup_address,dropoff_address,vehicle_type,scheduled_at,notes,admin_notes,status,assigned_driver_id,fare,estimated_fare,payment,payment_method,created_at';

export async function fetchAllRides(supabaseClient) {
  const runAppQuery = (selectColumns) =>
    supabaseClient
      .from('ride_requests')
      .select(selectColumns)
      .order('requested_at', { ascending: false })
      .limit(500);

  let appResult = await runAppQuery(APP_RIDE_SELECT);
  if (
    appResult.error &&
    /distance_km/i.test(appResult.error.message || '')
  ) {
    appResult = await runAppQuery(
      'id,rider_name,rider_phone,pickup,dropoff,pickup_at,requested_at,status,driver_id,driver_name,vehicle_number,vehicle_name,car_name,fare,payment,product,admin_note'
    );
  }

  const webResult = await supabaseClient
    .from('rides')
    .select(WEB_RIDE_SELECT)
    .order('created_at', { ascending: false })
    .limit(200);

  const messages = [];
  let appRows = [];
  let webRows = [];

  if (appResult.error) {
    const message = (appResult.error.message || '').toLowerCase();
    if (message.includes('ride_requests') || appResult.error.code === '42P01') {
      messages.push('App bookings table (ride_requests) was not found.');
    } else if (
      appResult.error.code === '42501' ||
      message.includes('permission') ||
      message.includes('row-level security')
    ) {
      messages.push(
        'Cannot read app bookings. Run supabase/fix-ride-board-app-bookings.sql, then refresh.'
      );
    } else {
      messages.push(appResult.error.message);
    }
  } else {
    appRows = (appResult.data || []).map(mapAppRide);
  }

  if (!webResult.error) {
    webRows = (webResult.data || []).map(mapWebRide);
  }

  const rides = [...appRows, ...webRows].sort(
    (a, b) => Date.parse(b.created_at || 0) - Date.parse(a.created_at || 0)
  );

  return { rides, error: messages.join(' ') };
}

export async function fetchRideBySource(supabaseClient, source, id) {
  if (source === 'web') {
    const { data, error } = await supabaseClient.from('rides').select('*').eq('id', id).maybeSingle();
    if (error) return { ride: null, error: error.message };
    return { ride: data ? mapWebRide(data) : null, error: '' };
  }

  const { data, error } = await supabaseClient
    .from('ride_requests')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    const message = (error.message || '').toLowerCase();
    if (error.code === '42501' || message.includes('permission') || message.includes('row-level')) {
      return {
        ride: null,
        error:
          'Cannot read this ride. Run supabase/fix-ride-board-app-bookings.sql so your admin has is_admin=true.',
      };
    }
    return { ride: null, error: error.message };
  }

  return { ride: data ? mapAppRide(data) : null, error: '' };
}

/** Same 15-minute rule as the passenger app (scheduled rides stay `scheduled` after assign). */
export function isFutureScheduledPickup(pickupAt, now = Date.now()) {
  const at = Date.parse(String(pickupAt || ''));
  return Number.isFinite(at) && at - now > 15 * 60 * 1000;
}

function phoneLookupVariants(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  const variants = new Set();
  if (digits) variants.add(digits);
  if (digits.length === 10) variants.add(digits);
  if (digits.length > 10) variants.add(digits.slice(-10));
  return [...variants].filter(Boolean);
}

/** Map hiring-site driver phone → Saarthi app profile (ride_requests.driver_id is auth user id). */
export async function lookupAppProfileForHiringDriver(supabaseClient, hiringDriver) {
  if (!supabaseClient || !hiringDriver) return null;
  for (const phone of phoneLookupVariants(hiringDriver.phone)) {
    const { data } = await supabaseClient
      .from('profiles')
      .select('id, first_name, last_name, avatar_url')
      .eq('phone', phone)
      .maybeSingle();
    if (data?.id) return data;
  }
  return null;
}

export function hiringDriverVehicleMeta(hiringDriver) {
  const vehicle =
    Array.isArray(hiringDriver?.vehicles) && hiringDriver.vehicles.length
      ? hiringDriver.vehicles[0]
      : null;
  const vehicleNumber = hiringDriver?.vehicle_number || vehicle?.vehicle_number || null;
  const vehicleName =
    hiringDriver?.vehicle_name ||
    [hiringDriver?.vehicle_make || vehicle?.vehicle_make, hiringDriver?.vehicle_model || vehicle?.vehicle_model]
      .filter(Boolean)
      .join(' ') ||
    vehicle?.vehicle_type ||
    hiringDriver?.vehicle_type ||
    null;
  return { vehicleNumber, vehicleName };
}

/**
 * Payload for public.ride_requests so the rider app realtime UI shows assigned driver
 * (mirrors acceptRideOnRequest in the Saarthi passenger app).
 */
export async function buildAppRideAssignPayload(supabaseClient, ride, hiringDriver, adminNote) {
  const { vehicleNumber, vehicleName } = hiringDriverVehicleMeta(hiringDriver);
  const fromApp =
    hiringDriver?.origin === 'app' ||
    Boolean(String(hiringDriver?.app_profile_id || '').trim());
  let profile = null;
  if (fromApp) {
    const profileId = String(hiringDriver.app_profile_id || hiringDriver.id || '').trim();
    if (profileId) {
      profile = {
        id: profileId,
        avatar_url: hiringDriver.driver_photo_url || null,
      };
    }
  }
  if (!profile) {
    profile = await lookupAppProfileForHiringDriver(supabaseClient, hiringDriver);
  }
  const pickupAt = ride?.scheduled_at || ride?.raw?.pickup_at || null;
  const keepScheduled = isFutureScheduledPickup(pickupAt);
  const nowIso = new Date().toISOString();
  const noteParts = [
    adminNote?.trim(),
    fromApp
      ? `App driver: ${hiringDriver.full_name} (${hiringDriver.phone}) · app_profile_id=${hiringDriver.app_profile_id || hiringDriver.id}`
      : `Hiring driver: ${hiringDriver.full_name} (${hiringDriver.phone}) · hiring_driver_id=${hiringDriver.id}`,
  ].filter(Boolean);

  const payload = {
    driver_id: profile?.id ? String(profile.id) : null,
    driver_name: hiringDriver.full_name,
    vehicle_number: vehicleNumber,
    vehicle_name: vehicleName,
    driver_photo_url: profile?.avatar_url || hiringDriver.driver_photo_url || null,
    status: keepScheduled ? 'scheduled' : 'accepted',
    accepted_at: nowIso,
    offered_driver_id: null,
    offered_at: null,
    admin_note: noteParts.join(' · ') || null,
  };

  if (!keepScheduled) {
    payload.started_at = nowIso;
  }

  return payload;
}

export function buildWebRideAssignPayload(hiringDriver, adminNote) {
  const { vehicleNumber, vehicleName } = hiringDriverVehicleMeta(hiringDriver);
  const noteParts = [
    adminNote?.trim(),
    `Hiring driver: ${hiringDriver.full_name} (${hiringDriver.phone})`,
    `hiring_driver_id=${hiringDriver.id}`,
    vehicleName ? `Vehicle: ${vehicleName}` : null,
    vehicleNumber ? `Plate: ${vehicleNumber}` : null,
  ].filter(Boolean);

  return {
    assigned_driver_id: hiringDriver.hiring_id || hiringDriver.id,
    status: 'assigned',
    assigned_at: new Date().toISOString(),
    admin_notes: noteParts.join(' · ') || null,
  };
}

export function buildAppRideStatusPayload(boardStatus, ride, adminNote, hiringDriverOptional) {
  const statusMap = {
    open: 'requested',
    assigned: 'accepted',
    live: 'in_trip',
    completed: 'completed',
    cancelled: 'cancelled',
  };
  const payload = {
    status: statusMap[boardStatus] || 'requested',
    admin_note: adminNote?.trim() || null,
  };

  if (boardStatus === 'open') {
    payload.driver_id = null;
    payload.driver_name = null;
    payload.vehicle_number = null;
    payload.vehicle_name = null;
    payload.driver_photo_url = null;
    payload.accepted_at = null;
    payload.started_at = null;
    payload.offered_driver_id = null;
    payload.offered_at = null;
  }

  if (boardStatus === 'assigned' && hiringDriverOptional) {
    payload.status = isFutureScheduledPickup(ride?.scheduled_at || ride?.raw?.pickup_at)
      ? 'scheduled'
      : 'accepted';
    payload.driver_name = hiringDriverOptional.full_name;
    const { vehicleNumber, vehicleName } = hiringDriverVehicleMeta(hiringDriverOptional);
    payload.vehicle_number = vehicleNumber;
    payload.vehicle_name = vehicleName;
    payload.accepted_at = new Date().toISOString();
  }

  if (boardStatus === 'completed') {
    payload.completed_at = new Date().toISOString();
  }
  if (boardStatus === 'cancelled') {
    payload.cancelled_at = new Date().toISOString();
    payload.cancelled_by = 'admin';
  }

  return payload;
}

/** Expo push types supported by Saarthi `notify-rider` edge function. */
export function adminAppNotifyTypeForBoardStatus(boardStatus, ride) {
  switch (boardStatus) {
    case 'open':
      return 'driver_unassigned';
    case 'assigned': {
      const pickupAt = ride?.scheduled_at || ride?.raw?.pickup_at;
      return isFutureScheduledPickup(pickupAt) ? 'scheduled_driver_accepted' : 'driver_accepted';
    }
    case 'live':
      return 'trip_started';
    case 'completed':
      return 'driver_dropped_off';
    case 'cancelled':
      return 'ride_cancelled';
    default:
      return null;
  }
}

/** Push the assigned Saarthi app driver with pickup time and full ride details. */
export async function notifyAssignedAppDriver(supabaseClient, ride, hiringDriver) {
  if (!supabaseClient || !ride?.id) return { ok: false, error: 'Missing ride' };

  const fromApp =
    hiringDriver?.origin === 'app' ||
    Boolean(String(hiringDriver?.app_profile_id || '').trim()) ||
    ride?.source === 'app';
  if (!fromApp && hiringDriver && hiringDriver.origin === 'web' && !hiringDriver.app_profile_id) {
    return { ok: false, error: 'not_app_driver' };
  }

  const driverId = String(
    hiringDriver?.app_profile_id ||
      hiringDriver?.id ||
      ride.assigned_driver_id ||
      ''
  ).trim();

  try {
    const { data, error } = await supabaseClient.functions.invoke('notify-driver-assignment', {
      body: {
        rideId: String(ride.id),
        source: ride.source === 'web' ? 'web' : 'app',
        driverId,
      },
    });
    if (error) {
      console.warn('[admin] notify-driver-assignment:', error.message || error);
      return { ok: false, error: error.message || 'Notify failed' };
    }
    return { ok: true, data };
  } catch (err) {
    console.warn('[admin] notify-driver-assignment failed', err);
    return { ok: false, error: err instanceof Error ? err.message : 'Notify failed' };
  }
}

/** Tell the rider app (push + Realtime) after admin changes an app booking. */
export async function notifyRiderOfAdminAppStatusChange(
  supabaseClient,
  ride,
  boardStatus,
  previousBoardStatus
) {
  if (!supabaseClient || !ride?.id || ride.source !== 'app') return;

  const type = adminAppNotifyTypeForBoardStatus(boardStatus, ride);
  if (!type) return;

  if (
    type === 'driver_unassigned' &&
    !['assigned', 'live'].includes(String(previousBoardStatus || ''))
  ) {
    return;
  }

  try {
    const { error } = await supabaseClient.functions.invoke('notify-rider', {
      body: { rideId: String(ride.id), type },
    });
    if (error) {
      console.warn('[admin] notify-rider:', error.message || error);
    }
  } catch (err) {
    console.warn('[admin] notify-rider failed', err);
  }
}

/** Removes all completed and cancelled trips (both app and web). Requires purge SQL in Supabase. */
export async function purgeCompletedCancelledRides(supabaseClient) {
  const { data, error } = await supabaseClient.rpc('purge_completed_cancelled_rides');

  if (!error) {
    const ridesDeleted = Number(data?.rides_deleted ?? 0);
    const appDeleted = Number(data?.ride_requests_deleted ?? 0);
    return {
      ridesDeleted,
      appDeleted,
      total: ridesDeleted + appDeleted,
      error: '',
    };
  }

  const message = error.message || 'Purge failed';
  if (!/purge_completed_cancelled_rides|schema cache|could not find/i.test(message)) {
    return { ridesDeleted: 0, appDeleted: 0, total: 0, error: message };
  }

  const webResult = await supabaseClient
    .from('rides')
    .delete()
    .in('status', ['completed', 'cancelled'])
    .select('id');

  if (webResult.error) {
    return {
      ridesDeleted: 0,
      appDeleted: 0,
      total: 0,
      error: `${webResult.error.message}. Run supabase/delete-completed-cancelled-rides.sql in Supabase SQL editor.`,
    };
  }

  const appResult = await supabaseClient
    .from('ride_requests')
    .delete()
    .in('status', ['completed', 'cancelled', 'Completed', 'Cancelled'])
    .select('id');

  if (appResult.error) {
    return {
      ridesDeleted: webResult.data?.length ?? 0,
      appDeleted: 0,
      total: webResult.data?.length ?? 0,
      error: `${appResult.error.message}. Run supabase/delete-completed-cancelled-rides.sql in Supabase SQL editor.`,
    };
  }

  const ridesDeleted = webResult.data?.length ?? 0;
  const appDeleted = appResult.data?.length ?? 0;
  return { ridesDeleted, appDeleted, total: ridesDeleted + appDeleted, error: '' };
}
